import React from 'react';
import PropTypes from 'prop-types';

/**
 * Card Search sidebar module - search and filter cards
 */
function CardSearch({
  isCollapsed,
  onToggle,
  searchQuery,
  onSearchChange,
  onSearch,
  searchLoading,
  searchError,
  searchResults,
  hasMoreResults,
  onResultsScroll,
  onAddCard,
  getProxyImageUrl,
  sidebarExpanded,
  // Filter props
  collapseFilters,
  onToggleFilters,
  selectedCardTypes,
  onCardTypesChange,
  selectedCharacters,
  onCharactersChange,
  selectedLevels,
  onLevelsChange,
  onClearFilters,
}) {
  return (
    <div className="sidebar-section search-section">
      <div className="section-header">
        <h4>🔍 Search Cards</h4>
        <button 
          className="collapse-btn" 
          onClick={onToggle}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
      </div>

      {!isCollapsed && (
        <>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && onSearch()}
            placeholder="Search by card name..."
          />
          <button onClick={onSearch} disabled={searchLoading} className="search-btn">
            {searchLoading ? 'Searching...' : 'Search'}
          </button>

          {/* Filters Section */}
          <div className="filters-container">
            <button 
              className="filters-toggle-btn"
              onClick={onToggleFilters}
            >
              🔽 Filters {selectedCardTypes.length + selectedCharacters.length + selectedLevels.length > 0 && `(${selectedCardTypes.length + selectedCharacters.length + selectedLevels.length})`}
            </button>

            {!collapseFilters && (
              <div className="filters-panel">
                {/* Card Type Filter */}
                <div className="filter-group">
                  <h5>Card Type</h5>
                  <select
                    className="filter-dropdown"
                    value={selectedCardTypes[0] || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        onCardTypesChange([e.target.value]);
                      } else {
                        onCardTypesChange([]);
                      }
                    }}
                  >
                    <option value="">All Types</option>
                    <option value="Ultra Hero">Ultra Hero</option>
                    <option value="Kaiju">Kaiju</option>
                    <option value="Scene">Scene</option>
                  </select>
                </div>

                {/* Character Filter */}
                <div className="filter-group">
                  <h5>Character Name</h5>
                  <input
                    type="text"
                    className="character-filter-input"
                    placeholder="Filter by character..."
                    value={selectedCharacters.join(', ')}
                    onChange={(e) => {
                      const chars = e.target.value.split(',').map(c => c.trim()).filter(Boolean);
                      onCharactersChange(chars);
                    }}
                  />
                </div>

                {/* Level Filter */}
                <div className="filter-group">
                  <h5>Card Level</h5>
                  <select
                    className="filter-dropdown"
                    value={selectedLevels[0] || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        onLevelsChange([e.target.value]);
                      } else {
                        onLevelsChange([]);
                      }
                    }}
                  >
                    <option value="">All Levels</option>
                    {['1', '2', '3', '4', '5', '6', '7'].map(level => (
                      <option key={level} value={level}>
                        Level {level}
                      </option>
                    ))}
                  </select>
                </div>

                <button className="clear-filters-btn" onClick={onClearFilters}>
                  Clear All Filters
                </button>
              </div>
            )}
          </div>

          {searchError && <div className="search-error">{searchError}</div>}

          {searchResults.length > 0 && (
            <div className="search-results" onScroll={onResultsScroll}>
              {searchResults.map((card, index) => (
                <div 
                  key={index} 
                  className={`search-result-item${card.feature === 'scene' ? ' scene-card' : ''}`}
                  onClick={() => onAddCard(card)}
                >
                  <img
                    src={getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full)}
                    alt={card.name}
                    className="result-thumbnail"
                    loading="lazy"
                  />
                  {sidebarExpanded ? (
                    // Expanded state: overlay display
                    <div className="search-result-overlay">
                      <div className="overlay-content">
                        <h4>{card.name}</h4>
                        <p className="result-number">{card.number}</p>
                        <p className="result-meta">
                          {card.characterName && <span className="character-tag">{card.characterName}</span>}
                          {typeof card.rarity === 'string' ? (
                            <span className="rarity-tag">{card.rarity}</span>
                          ) : card.rarity?.description && (
                            <span className="rarity-tag">{card.rarity.description}</span>
                          )}
                        </p>
                        <button className="add-btn-overlay">
                          Add to Deck
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Collapsed state: side-by-side display like recommendations
                    <>
                      <div className="result-info">
                        <strong>{card.name}</strong>
                        <p className="result-number">{card.number}</p>
                        <p className="result-meta">
                          {card.characterName && <span className="character-tag">{card.characterName}</span>}
                          {typeof card.rarity === 'string' ? (
                            <span className="rarity-tag">{card.rarity}</span>
                          ) : card.rarity?.description && (
                            <span className="rarity-tag">{card.rarity.description}</span>
                          )}
                        </p>
                      </div>
                      <button className="add-btn">
                        Add
                      </button>
                    </>
                  )}
                </div>
              ))}
              {searchLoading && searchResults.length > 0 && (
                <div className="loading-indicator">
                  <div className="spinner"></div>
                  <span>Loading more cards...</span>
                </div>
              )}
              {!hasMoreResults && searchResults.length > 0 && (
                <div className="end-of-results">
                  No more cards to load
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

CardSearch.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  searchError: PropTypes.string,
  searchResults: PropTypes.array.isRequired,
  hasMoreResults: PropTypes.bool.isRequired,
  onResultsScroll: PropTypes.func.isRequired,
  onAddCard: PropTypes.func.isRequired,
  getProxyImageUrl: PropTypes.func.isRequired,
  sidebarExpanded: PropTypes.bool.isRequired,
  collapseFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  selectedCardTypes: PropTypes.array.isRequired,
  onCardTypesChange: PropTypes.func.isRequired,
  selectedCharacters: PropTypes.array.isRequired,
  onCharactersChange: PropTypes.func.isRequired,
  selectedLevels: PropTypes.array.isRequired,
  onLevelsChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export default CardSearch;
